import { Box, Button, Chip, Slider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AGE_RANGE_BOUNDS, AgeRangeUnit } from '@kheops/utils';

interface AgeRangeStepProps {
  question: string;
  isLoading: boolean;
  onConfirm: (rangeLabel: string) => void;
  onBack?: () => void;
}

const AGE_VALUE_KEYS: Record<AgeRangeUnit, string> = {
  years: 'products:naming_wizard.age_value_years',
  months: 'products:naming_wizard.age_value_months',
};

export default function AgeRangeStep({ question, isLoading, onConfirm, onBack }: AgeRangeStepProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products']);
  const [unit, setUnit] = useState<AgeRangeUnit>('years');
  const [range, setRange] = useState<[number, number]>([AGE_RANGE_BOUNDS.years.min, AGE_RANGE_BOUNDS.years.max]);
  const bounds = AGE_RANGE_BOUNDS[unit];

  const formatRangeLabel = (values: [number, number]): string => t('products:naming_wizard.age_range', {
    min: t(AGE_VALUE_KEYS[unit], { count: values[0] }),
    max: t(AGE_VALUE_KEYS[unit], { count: values[1] }),
  });

  const handleUnitChange = (newUnit: AgeRangeUnit): void => {
    setUnit(newUnit);
    setRange([AGE_RANGE_BOUNDS[newUnit].min, AGE_RANGE_BOUNDS[newUnit].max]);
  };

  const handleRangeChange = (_event: Event, newValue: number | number[]): void => {
    if (Array.isArray(newValue)) {
      setRange([newValue[0], newValue[1]]);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="titleMedium">{question}</Typography>
      <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
        <Chip
          variant="filter"
          className={unit === 'years' ? 'Kheops-selected' : ''}
          label={t('products:naming_wizard.age_unit_years')}
          onClick={() => handleUnitChange('years')}
        />
        <Chip
          variant="filter"
          className={unit === 'months' ? 'Kheops-selected' : ''}
          label={t('products:naming_wizard.age_unit_months')}
          onClick={() => handleUnitChange('months')}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
        <Typography variant="labelLarge">
          {formatRangeLabel(range)}
        </Typography>
        <Slider
          value={range}
          onChange={handleRangeChange}
          min={bounds.min}
          max={bounds.max}
          step={1}
          disableSwap
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        {onBack && (
          <Button variant="outlined" sx={{ height: 40 }} disabled={isLoading} onClick={onBack}>
            {t('common:previous')}
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ height: 40 }}
          loading={isLoading}
          onClick={() => onConfirm(formatRangeLabel(range))}
        >
          {t('common:confirm')}
        </Button>
      </Box>
    </Box>
  );
}
